import React from "react";

import { useBeds } from "./query/useBeds";
import { useRooms } from "./query/useRooms";
import { useWards } from "./query/useWards";
import { Bed } from "../interfaces/BedInterface";
import { IPD } from "../interfaces/IpdInterface";
import { Room } from "../interfaces/RoomInterface";
import { TWardType, Ward } from "../interfaces/WardInterface";
import { useBedAssignments } from "./query/useBedAssignments";

export interface ExtendedBed extends Bed {
  isOccupied: boolean;
  activeIpd: IPD | null;
  bedSlug: string;
  room: Room;
  ward: Ward;
}

export function useWardTypeData({ wardType }: { wardType?: TWardType } = {}): {
  extendedBeds: ExtendedBed[];
  wards: Ward[];
  rooms: Room[];
} {
  const { data: beds = [] } = useBeds();
  const { data: rooms = [] } = useRooms(wardType ? { wardType } : undefined);
  const { data: wards = [] } = useWards(wardType ? { type: wardType } : undefined);
  const { data: activeBedAssignments = [] } = useBedAssignments({ active: true });

  const extendedBeds = React.useMemo(() => {
    const roomMap = new Map(rooms.map((room) => [Number(room.id), room]));
    const wardMap = new Map(wards.map((ward) => [Number(ward.id), ward]));

    const assignmentMap = new Map(
      activeBedAssignments.map((assignment) => [Number(assignment.bedId), assignment])
    );

    return beds.map((bed) => {
      const activeBedAssignment = assignmentMap.get(Number(bed.id));
      const room = bed.room || roomMap.get(Number(bed.roomId));
      const ward = room?.ward || wardMap.get(Number(room?.wardId));

      return {
        ...bed,
        isOccupied: !!activeBedAssignment,
        activeIpd: activeBedAssignment?.ipd ?? null,
        bedSlug: [ward?.shortCode, room?.code, bed.code, bed.number]
          .filter(Boolean)
          .join("-")
          .toLowerCase(),
        room: room ?? bed.room,
        ward: ward ?? bed.room?.ward
      } as ExtendedBed;
    });
  }, [beds, rooms, wards, activeBedAssignments]);

  return {
    wards,
    rooms,
    extendedBeds
  };
}
